import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { EntityTypes } from '../../../../new-lib/constants/common';
import { Image, EntityDetailsTypes } from '../../../../new-lib/types/entity';
import { getImagePath } from '../../../../helpers/common';
import { getImageSources } from '../../../../helpers/images';
import GalleryIcon from '../../../../public/assets/icons/entity/GalleryIcon';
import MasterPlanIcon from '../../../../public/assets/icons/entity/MasterPlanIcon';
import QuickView from '../../../../new-components/display-system/QuickView';
import Map from '../../../../new-components/map/Map';
import EntityDetail from './EntityDetail';
import { logEvent } from '../../../../new-lib/analytics';
import ViewOnMapIcon from '../../../../new-components/svg-icons/ViewOnMapIcon';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { setSelectedCompound } from '../../../../redux/slices/mapSlice';
import { DefaultMapEntity } from '../../../map/types';
import VideoIcon from '../../../../public/assets/icons/entity/VideoIcon';
import FloorPlansIcon from '../../../../public/assets/icons/entity/FloorPlansIcon';
import { useVideo } from '../../../../hooks/Video';

interface ChildProps {
  entityType: EntityTypes;
  galleryImages: Image[] | string[];
  floorPlanImages?: Image[];
  masterPlanImage: string;
  walkthroughVideo?: string;
  mapData: DefaultMapEntity;
  propertyData?: {
    property_id: number;
    compound_id: number;
    compound_name: string;
    sale_type: string;
    property_type: string;
  };
}

const EntityDetails = ({
  entityType,
  galleryImages,
  floorPlanImages,
  masterPlanImage,
  walkthroughVideo,
  mapData,
  propertyData
}: ChildProps): JSX.Element => {
  const router = useRouter();
  const { t } = useTranslation(['entity']);
  const [showDetail, setShowDetail] = useState(false);
  const [detailType, setDetailType] = useState<EntityDetailsTypes>();
  const [GalleryComponent, setGalleryComponent] = useState<any>(null);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { handleFullScreenVideo } = useVideo({
    videoRef,
    propertyData,
    setIsVideoExpanded,
    eventName: 'virtual_tour_click',
    featureName: 'walkthrough_video_property_page'
  });

  const dispatch = useDispatch();
  useEffect(() => {
    if (showDetail && !GalleryComponent) {
      const Gallery = dynamic(
        () => import('../../../../new-components/Gallery')
      );
      setGalleryComponent(Gallery);
    }

    return () => {
      setGalleryComponent(null);
      document.documentElement?.classList?.remove('fslightbox-open');
    };
  }, [showDetail]);

  const handleShowDetail = (detailType?: EntityDetailsTypes) => {
    setShowDetail(!showDetail);
    setDetailType(detailType);
    logEvent.click(
      `${detailType}_click`,
      `${entityType}DetailsSection`,
      'other'
    );
  };

  const handleMapAction = () => {
    if (mapData?.geometry?.coordinates) {
      dispatch(setSelectedCompound(mapData));
      router.push(`/search?view=map`);
    } else {
      handleShowDetail(EntityDetailsTypes.MAP);
    }
  };

  const getImages = (detailType?: EntityDetailsTypes) => {
    switch (detailType) {
      case EntityDetailsTypes.FLOOR_PLAN:
        return getImageSources(floorPlanImages);
      case EntityDetailsTypes.GALLERY:
        return getImageSources(galleryImages);
      case EntityDetailsTypes.MASTER_PLAN:
        return [getImagePath(masterPlanImage)];
      default:
        return undefined;
    }
  };

  return (
    <DetailsContainer>
      <h3 className="headline-3 entity-title">{t('details')}</h3>
      <div className="entities-container">
        {walkthroughVideo && (
          <EntityDetail
            clickHandler={() => {
              handleFullScreenVideo('virtual_tour_button');
            }}
            icon={<VideoIcon />}
            label={t('virtual_tour')}
            displayNew
          />
        )}
        {galleryImages.length > 0 && (
          <EntityDetail
            clickHandler={() => {
              handleShowDetail(EntityDetailsTypes.GALLERY);
            }}
            icon={<GalleryIcon alternateIcon />}
            label={t('gallery')}
          />
        )}
        {mapData?.long > 0 && mapData?.lat > 0 && (
          <EntityDetail
            clickHandler={() => handleMapAction()}
            icon={<ViewOnMapIcon alternateIcon />}
            label={t('view_on_map')}
            // displayNew={mapData?.geometry?.coordinates !== undefined}
          />
        )}
        {entityType == EntityTypes.PROPERTY &&
          floorPlanImages &&
          floorPlanImages?.length > 0 && (
            <EntityDetail
              clickHandler={() => {
                handleShowDetail(EntityDetailsTypes.FLOOR_PLAN);
              }}
              icon={<FloorPlansIcon alternateIcon />}
              label={t('floor_plan')}
            />
          )}

        {masterPlanImage && (
          <EntityDetail
            clickHandler={() => {
              handleShowDetail(EntityDetailsTypes.MASTER_PLAN);
            }}
            icon={<MasterPlanIcon alternateIcon />}
            label={t('master_plan')}
          />
        )}
      </div>
      <QuickView isOpen={showDetail} setIsOpen={setShowDetail} isMap={true}>
        {detailType == EntityDetailsTypes.MAP && (
          <Map
            mapData={{ long: mapData?.long, lat: mapData?.lat }}
            name={mapData?.name}
          />
        )}
        {detailType != EntityDetailsTypes.MAP && GalleryComponent && (
          <GalleryContainer>
            <GalleryComponent
              show={showDetail}
              sources={getImages(detailType)}
              detailType={detailType}
              actionButtonText={t('virtual_tour')}
              actionButtonClickHandler={() => {
                handleFullScreenVideo('floor_plan_button');
              }}
              isActionButtonNewTag
            />
            {detailType === EntityDetailsTypes.FLOOR_PLAN &&
              walkthroughVideo && (
                <ButtonContainer locale={router.locale}>
                  <div
                    className="action-button"
                    onClick={() => {
                      handleFullScreenVideo('floor_plan_button');
                    }}
                  >
                    <span className={'new-text'}>{t('virtual_tour')}</span>
                  </div>
                </ButtonContainer>
              )}
          </GalleryContainer>
        )}
      </QuickView>

      {walkthroughVideo && (
        <HiddenVideo
          ref={videoRef}
          src={walkthroughVideo}
          playsInline
          preload="auto"
          controls={isVideoExpanded}
          muted={!isVideoExpanded}
          loop
        />
      )}
    </DetailsContainer>
  );
};

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  .entities-container {
    display: flex;
    flex-direction: row;
    gap: 16px;
  }
`;

const HiddenVideo = styled.video`
  visibility: hidden;
  width: 1px;
  height: 1px;

  &:fullscreen {
    visibility: visible;
    width: 100%;
    height: 100%;
  }
`;

const GalleryContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ButtonContainer = styled.div<any>`
  position: relative;
  margin-bottom: 30px;
  z-index: 999;

  .action-button {
    cursor: pointer;
    display: inline-flex;
    padding: 12px 60px;
    justify-content: center;
    align-items: center;
    gap: var(--Gap-Gap-8, 8px);
    border-radius: 8px;
    border: 1px solid var(--Nutral-N-7-Border, #c6d0de);
    background: var(--White-Black-White---Black, #fff);

    span {
      color: #015c9a;
      font-size: 20px;
      font-family: materialRegular;
      &.new-text {
        position: relative;
        &::after {
          content: ${props => (props.locale === 'ar' ? '"جديد"' : '"New"')};
          position: absolute;
          top: -20px;
          display: flex;
          justify-content: center;
          align-items: center;
          right: -65px;
          color: ${props => props.theme.light.colors.white};
          background-color: ${props => props.theme.light.colors.primaryOrange};
          width: 38px;
          height: 18px;
          border-radius: 175px;
          color: #fff;
          text-align: center;
          font-family: materialLight;
          font-size: 12px;
          text-transform: capitalize;
        }
      }
    }
  }
`;

export default EntityDetails;
